import {
  ADD_EMPTY_OWNER_PLACEHOLDER,
  OfferAction,
  REMOVE_OWNER_PLACEHOLDER,
  SET_ACCESS_TOKEN_ERROR,
  SET_HAS_OFFER,
  SET_OFFER_DATA,
  SET_OFFER_IS_AGREED,
  SET_OFFER_SUBMIT_STATUS,
  SET_PERSONAL_LIABILITY_IS_AGREED,
  SET_REGULAR_MAIL_FOR_INVOICES,
  UPDATE_OFFER_DATA,
  UPDATE_OFFER_SIGNERS,
  UPDATE_OWNER,
  UPDATE_YOUR_OWN_SHARES,
} from '~/store/types/loanOfferTypes';
import {
  apiGetOffer,
  apiGetPublicOffer,
  apiPostOffer,
  apiPostPublicOffer,
} from '~/services/api';
import { offerDataMapper } from '~/helpers/mappers.helper';
import { ApiListData, LoanOwner, Offer, OfferUserData } from '~/Interfaces';
import { Dispatch } from 'redux';
import { CountryCode, GlobalErrorType } from '~/enums';
import {
  formatBankAccountNumber,
  formatPersonalNumber,
} from '~/helpers/formatters.helper';
import { pushToSentryAction } from '~/store/actions/sentryActions';
import { Signer } from '~/types/types';
import { AppThunk } from '../types/sharedTypes';
import { toggleGlobalErrorOn } from '../slices/globalError.slice';

// =========================== Action creators ===============================
export const setOfferData = (offerData: Offer[]): OfferAction => ({
  type: SET_OFFER_DATA,
  payload: offerData,
});

export const setHasOffer = (status: boolean): OfferAction => ({
  type: SET_HAS_OFFER,
  payload: status,
});

export const setOfferIsAgreed = (status: boolean): OfferAction => ({
  type: SET_OFFER_IS_AGREED,
  payload: status,
});

export const setPersonalLiabilityIsAgreed = (status: boolean): OfferAction => ({
  type: SET_PERSONAL_LIABILITY_IS_AGREED,
  payload: status,
});

export const setRegularMailForInvoices = (status: boolean): OfferAction => ({
  type: SET_REGULAR_MAIL_FOR_INVOICES,
  payload: status,
});

export const updateOfferData = (
  offerUserData: Partial<OfferUserData>
): OfferAction => ({
  type: UPDATE_OFFER_DATA,
  payload: offerUserData,
});

export const updateOfferSigners = (
  offerSigner: Partial<Signer>
): OfferAction => ({
  type: UPDATE_OFFER_SIGNERS,
  payload: offerSigner,
});

export const updateOfferOwner = (offerOwner: LoanOwner): OfferAction => ({
  type: UPDATE_OWNER,
  payload: offerOwner,
});

export const updateOfferYourOwnShares = (offerYourOwnShares: string) => ({
  type: UPDATE_YOUR_OWN_SHARES,
  payload: offerYourOwnShares,
});

export const addEmptyOwnerPlaceholder = (): OfferAction => ({
  type: ADD_EMPTY_OWNER_PLACEHOLDER,
  payload: null,
});

export const removeOwnerPlaceholder = (ownerId: string): OfferAction => ({
  type: REMOVE_OWNER_PLACEHOLDER,
  payload: ownerId,
});

const setOfferSubmitStatus = (status: boolean): OfferAction => ({
  type: SET_OFFER_SUBMIT_STATUS,
  payload: status,
});

const loanOfferhasAccessTokenError = (status: boolean): OfferAction => ({
  type: SET_ACCESS_TOKEN_ERROR,
  payload: status,
});

const handleOfferData = (data: any[], dispatch: Dispatch) => {
  const flatOffers = data.reduce((acc, val) => {
    if (val.offer) {
      return acc.concat(val.offer);
    }
    return acc;
  }, []);

  if (flatOffers && flatOffers.length) {
    const offers = flatOffers.map((offerData: any) =>
      offerDataMapper(offerData)
    );
    dispatch(setOfferData(offers));
    dispatch(setHasOffer(true));
  } else {
    dispatch(setHasOffer(false));
  }
};

const transformOfferDetails = (
  details: OfferUserData,
  market: CountryCode
) => ({
  ...details,
  owners: details.owners.map((owner) => ({
    ...owner,
    personalNumber: formatPersonalNumber(owner.personalNumber, market),
  })),
  accountNumber: formatBankAccountNumber(details.accountNumber, market),
});

// ============================== Thunks ================================
export const sendOfferData = (
  offerDetails: OfferUserData,
  hash: string | null,
  market: CountryCode,
  clientId: number | null
): AppThunk => (dispatch) => {
  dispatch(setOfferSubmitStatus(true));
  let endPoint: Function;
  let sentryCategory: string;
  if (hash && market && clientId) {
    endPoint = apiPostPublicOffer;
    sentryCategory = 'apiPostPublicOffer';
  } else {
    endPoint = apiPostOffer;
    sentryCategory = 'apiPostOffer';
  }
  const transformedOfferDetails = transformOfferDetails(offerDetails, market);

  endPoint(transformedOfferDetails, hash as string, market as string, clientId)
    .then((res: any) => {
      const { data } = res;
      if (data && data.message) {
        // Throw global error since we do not have any other way of displaying specific API errors at the moment.
        dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
        throw new Error(data.message);
      }

      if (data) {
        window.location.assign(data.signURL);
      }
    })
    .catch((err: any) => {
      dispatch(
        pushToSentryAction(err, sentryCategory, {
          pn: transformedOfferDetails.personalNumber,
        })
      );
      dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
    })
    .finally(() => {
      dispatch(setOfferSubmitStatus(false));
    });
};

export const fetchOfferData = (companyId: number): AppThunk => async (
  dispatch
) => {
  try {
    const res = await apiGetOffer(companyId);
    const offers = res.data.offer?.map((o: any) => offerDataMapper(o));

    if (offers && offers.length) {
      dispatch(setOfferData(offers));
      dispatch(setHasOffer(true));
    } else {
      dispatch(setHasOffer(false));
    }
  } catch (err) {
    if (err.message === 'No ACCESSS_TOKEN found!') {
      dispatch(loanOfferhasAccessTokenError(true));
    } else {
      dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
    }
    dispatch(pushToSentryAction(err, 'apiGetOffer'));
  }
};

// For public offer page, pn is not available in redux and we have to send it in as an argument
export const fetchPublicOfferData = (
  companyId: number,
  market: string
): AppThunk => (dispatch) => {
  // For public offer page is always one single companyId is called but we keep the same format with Promise.all() for consistency
  const promises = [companyId].map((cId) => apiGetPublicOffer(cId, market));
  Promise.all(promises)
    .then((res: ApiListData) => {
      const data = res.map((obj) => obj.data);
      handleOfferData(data, dispatch);
    })
    .catch((err: any) => {
      if (err.message === 'No ACCESSS_TOKEN found!') {
        dispatch(loanOfferhasAccessTokenError(true));
      } else {
        dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
      }

      dispatch(pushToSentryAction(err, 'apiGetPublicOffer'));
    });
};
